import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { pagesEnum } from 'constants/ui.constants';
import _ from 'lodash';

import { fetchTranslations } from 'services/translation.services';

interface translationState {
	language: string;
	loading: boolean;
	translations: any;
	availableLanguages: any[] | null;
	showKeys: boolean;
	plansHebrewContent: any;
	welcomeHebrewContent: any;
}

const initialState: translationState = {
	language: localStorage.getItem('language') || 'he',
	loading: false,
	translations: (() => {
		try {
			return JSON.parse(localStorage.getItem('translations') || '{}');
		} catch {
			return {};
		}
	})(),
	availableLanguages: null,
	showKeys: false,
	plansHebrewContent: (() => {
		try {
			return JSON.parse(localStorage.getItem('plansHebrewContent') || '{}');
		} catch {
			return {};
		}
	})(),
	welcomeHebrewContent: (() => {
		try {
			return JSON.parse(localStorage.getItem('welcomeHebrewContent') || '{}');
		} catch {
			return {};
		}
	})(),
};

export const getLangAsync = createAsyncThunk(
	'translation/fetchLangAsync',
	async (lang: string) => {
		const { translations, plansHebrewContent, welcomeHebrewContent } = await fetchTranslations(lang);
		return { translations, plansHebrewContent, welcomeHebrewContent };
	},
	{
		condition: (lang, { getState, extra }) => {
			const { translation } = getState() as any;
			const fetchStatus = _.has(translation.translations, lang);
			if (fetchStatus) {
				return false;
			}
		},
	}
);

export const translationSlice = createSlice({
	name: 'translation',
	initialState,
	reducers: {
		toggleLang: (state, action: PayloadAction<string>) => {
			const plansOrWelcomePage = [`/${pagesEnum.PLANS}`, `/${pagesEnum.WELCOME}`].includes(
				window.location.pathname
			);
			state.language = plansOrWelcomePage ? 'he' : action.payload;
			if (!plansOrWelcomePage) {
				localStorage.setItem('language', state.language);
			}
		},
		setAvailableLanguages: (state, action: PayloadAction<any>) => {
			state.availableLanguages = action.payload.map((item: any) => {
				return { value: item.code, label: item.name };
			});
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		toggleShowKeys: (state, action: PayloadAction<boolean>) => {
			state.showKeys = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getLangAsync.pending, (state) => {
				if (!localStorage.getItem('translations')) {
					state.loading = true;
				}
			})
			.addCase(getLangAsync.fulfilled, (state, action) => {
				state.loading = false;
				const { translations, plansHebrewContent, welcomeHebrewContent } = action.payload;
				localStorage.setItem('translations', JSON.stringify(translations ?? {}));
				state.translations = translations;
				localStorage.setItem('plansHebrewContent', JSON.stringify(plansHebrewContent ?? {}));
				state.plansHebrewContent = plansHebrewContent;
				state.welcomeHebrewContent = welcomeHebrewContent;
				localStorage.setItem('welcomeHebrewContent', JSON.stringify(welcomeHebrewContent ?? {}));
			});
	},
});

export const { toggleLang, setAvailableLanguages, setLoading, toggleShowKeys } = translationSlice.actions;
export const selectLanguage = (state: any) => state.translation.language;
const translationsSelector = (state) => state.translation;
export const selectTranslations = createSelector([translationsSelector], (state: any) => {
	const translations = state.translations;
	const isShowKeysMode = state.showKeys;
	const plansHebrewContent = state.plansHebrewContent;
	const welcomeHebrewContent = state.welcomeHebrewContent;

	return function (key: string, fallback?: string) {
		if (!key?.length) {
			return '';
		}

		const keyFormat = `**${key}**`;
		if (isShowKeysMode) {
			return keyFormat;
		}
		const fallbackText = fallback || keyFormat;

		const location = window.location.pathname;

		if (location === `/${pagesEnum.PLANS}`) {
			return plansHebrewContent[key] || _.trim(translations[key], ' ') || fallbackText;
		} else if (location === `/${pagesEnum.WELCOME}`) {
			return welcomeHebrewContent[key] || _.trim(translations[key], ' ') || fallbackText;
		}
		return _.trim(translations[key], ' ') || fallbackText;
	};
});

export const selectImageUrl = createSelector([translationsSelector], (state: any) => {
	const translations = state.translations;
	return function (key: string) {
		return translations[key];
	};
});

export const getAvailableLanguages = (state: any) => state.translation.availableLanguages;

export const getLoadingLanguage = (state: any) => state.translation.loading;
export const getShowKeys = (state: any) => state.translation.showKeys;
export const getDir = (state: any) => (_.includes(state.translation.language, 'he') ? 'rtl' : 'ltr');

export default translationSlice.reducer;
